import React, { useState } from 'react';
import ApiClient, { normalizeApiHostname } from '../api/client';

interface SetupScreenProps {
  onSetup: (apiKey: string, hostname: string) => void;
  initialKey?: string;
  initialHostname?: string;
  createClient?: (apiKey: string, hostname: string) => Pick<ApiClient, 'listForms'>;
}

const SetupScreen: React.FC<SetupScreenProps> = ({
  onSetup,
  initialKey = '',
  initialHostname = '',
  createClient = (key, host) => new ApiClient(key, host),
}) => {
  const [apiKey, setApiKey] = useState(initialKey);
  const [hostname, setHostname] = useState(initialHostname);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!apiKey.trim()) {
      setError('Please enter an API key');
      return;
    }

    if (!hostname.trim()) {
      setError('Please enter the API hostname/URL');
      return;
    }

    setLoading(true);
    try {
      // Test the connection using ApiClient
      const normalizedHostname = normalizeApiHostname(hostname);
      const client = createClient(apiKey.trim(), normalizedHostname);
      await client.listForms();

      // If we got here, the connection works
      onSetup(apiKey.trim(), normalizedHostname);
    } catch (err) {
      console.error('Connection test failed:', err);
      let errorMessage = 'Failed to connect to API';

      if (err instanceof Error) {
        const message = err.message;

        // Check for axios response error (has response property)
        const axiosErr = err as any;
        if (axiosErr.response) {
          const status = axiosErr.response.status;
          if (status === 401) {
            errorMessage = 'Invalid API key';
          } else if (status === 404) {
            errorMessage = 'Invalid hostname - API not found at that URL';
          } else {
            errorMessage = `API error (${status}): ${axiosErr.response.data?.message || message}`;
          }
        } else if (message.includes('Network') || message.includes('fetch') || message.includes('ERR_')) {
          errorMessage = 'Could not connect to hostname - check the URL and try again';
        } else {
          errorMessage = `Connection failed: ${message}`;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="brand-lockup">
            <img
              src="/form-pipeline-icon.png"
              alt=""
              className="brand-mark"
              aria-hidden="true"
            />
            <div>
              <div className="brand-eyebrow">Operations Console</div>
              <h1>Form Pipeline</h1>
              <p className="subtitle">Configure your API connection</p>
            </div>
          </div>
        </div>
      </header>

      <main className="setup-page">
        <section className="setup-card">
          <div className="setup-card-header">
            <h2>Connect to backend</h2>
            <p>Enter the Form Pipeline API host and an API key for this browser.</p>
          </div>

            <form onSubmit={handleSubmit}>
              {error && (
                <div className="alert alert-error" style={{ marginBottom: '16px' }}>
                  <span>Error</span>
                  <span>{error}</span>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="hostname">API Hostname *</label>
                <input
                  id="hostname"
                  type="text"
                  value={hostname}
                  onChange={(e) => setHostname(e.target.value)}
                  placeholder="e.g., http://localhost:8000"
                  disabled={loading}
                  autoFocus
                />
                <small>The URL where this tenant's Form Pipeline API is running.</small>
              </div>

              <div className="form-group">
                <label htmlFor="apiKey">API Key *</label>
                <input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="sk_..."
                  disabled={loading}
                />
                <small>Your API key for authentication</small>
              </div>

              <button
                className="btn btn-primary setup-submit"
                type="submit"
                disabled={loading}
              >
                {loading ? 'Testing connection...' : 'Continue'}
              </button>
            </form>
        </section>
      </main>
    </div>
  );
};

export default SetupScreen;
